import { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

const translations = {
  ar: {
    // Navigation
    home: "الرئيسية",
    about: "من نحن",
    products: "المنتجات",
    contact: "تواصل معنا",
    login: "تسجيل الدخول",
    register: "إنشاء حساب",
    logout: "تسجيل الخروج",
    dashboard: "لوحة التحكم",
    myOrders: "طلباتي",
    favorites: "المفضلة",
    profile: "الملف الشخصي",
    
    // Hero
    heroTitle: "شركة بحر الرمال",
    heroSubtitle: "لإستيراد الأدوية والمعدات الطبية",
    heroDescription: "الموزع الرئيسي لمنتجات لينى للفيتامينات والمكملات الغذائية في ليبيا",
    browseProducts: "تصفح المنتجات",
    contactUs: "تواصل معنا",
    
    // Features
    qualityProducts: "منتجات عالية الجودة",
    qualityDesc: "أدوية ومستلزمات طبية من أفضل المصنعين العالميين",
    fastDelivery: "توصيل سريع",
    fastDeliveryDesc: "شبكة توزيع موثوقة في جميع أنحاء ليبيا",
    certifiedSafe: "معتمد وآمن",
    certifiedSafeDesc: "جميع المنتجات تلبي معايير الجودة والسلامة الدولية",
    since2012: "منذ 2012",
    since2012Desc: "أكثر من عقد من التميز في التوزيع الطبي",
    
    // Products
    featuredProducts: "منتجات مميزة",
    popularProducts: "المنتجات الأكثر مبيعاً",
    viewAll: "عرض الكل",
    addToCart: "أضف للسلة",
    add: "إضافة",
    categories: "التصنيفات",
    browseByCategory: "تصفح حسب التصنيف",
    allCategories: "جميع التصنيفات",
    search: "بحث",
    searchProducts: "البحث في المنتجات...",
    filters: "الفلاتر",
    clearAll: "مسح الكل",
    priceRange: "نطاق السعر",
    countryOfOrigin: "بلد المنشأ",
    allCountries: "جميع البلدان",
    noProductsFound: "لم يتم العثور على منتجات",
    tryAdjusting: "حاول تعديل الفلاتر أو كلمات البحث",
    clearFilters: "مسح الفلاتر",
    lowStock: "مخزون منخفض",
    inStock: "متوفر",
    outOfStock: "غير متوفر",
    
    // Product Detail
    orderViaWhatsApp: "طلب عبر واتساب",
    quantity: "الكمية",
    sku: "رمز المنتج",
    origin: "بلد المنشأ",
    expiry: "تاريخ الانتهاء",
    batch: "رقم الدفعة",
    relatedProducts: "منتجات ذات صلة",
    
    // About
    aboutUs: "من نحن",
    aboutCompany: "عن شركة بحر الرمال",
    aboutHeroDesc: "شركة توزيع الأدوية الموثوقة في ليبيا منذ 2012، نخدم مقدمي الرعاية الصحية بأدوية ومستلزمات طبية عالية الجودة",
    ourStory: "قصتنا",
    storyText1: "تأسست شركة بحر الرمال في طرابلس عام 2012، وأصبحت واحدة من الشركات الرائدة في توزيع الأدوية والمستلزمات الطبية في ليبيا. اسمنا يعني 'بحر الرمال' ويعكس جذورنا العميقة في الثقافة الليبية.",
    storyText2: "نحن متخصصون في استيراد وتوزيع الأدوية والفيتامينات والمكملات الغذائية والمعدات الطبية. كموزع رئيسي حصري لمنتجات لينى في ليبيا، نقدم منتجات رعاية صحية عالمية المستوى.",
    storyText3: "تمتد شبكة التوزيع لدينا في جميع أنحاء ليبيا، مما يضمن حصول مقدمي الرعاية الصحية في كل منطقة على المنتجات عالية الجودة التي يحتاجونها.",
    yearsOfService: "سنوات من الخدمة",
    keyMilestones: "المحطات الرئيسية",
    ourValues: "قيمنا",
    qualityFirst: "الجودة أولاً",
    qualityFirstDesc: "نوزع فقط المنتجات التي تلبي معايير الجودة الدولية",
    customerFocus: "التركيز على العملاء",
    customerFocusDesc: "بناء علاقات دائمة مع مقدمي الرعاية الصحية",
    reliableDelivery: "توصيل موثوق",
    reliableDeliveryDesc: "توصيل متسق وفي الوقت المحدد في جميع أنحاء ليبيا",
    globalSourcing: "مصادر عالمية",
    globalSourcingDesc: "شراكة مع مصنعين دوليين موثوقين",
    lynaePartnership: "شراكة لينى",
    lynaePartnershipDesc: "أصبحنا الموزع الحصري لفيتامينات لينى",
    lynaeDesc: "كموزع رئيسي حصري لمنتجات لينى في ليبيا، نقدم فيتامينات ومكملات أمريكية عالية الجودة",
    viewLynaeProducts: "عرض منتجات لينى",
    partnerWithUs: "كن شريكنا",
    companyFounded: "تأسيس الشركة",
    foundedDescription: "تأسست بحر الرمال في طرابلس، ليبيا",
    nationalExpansion: "التوسع الوطني",
    expansionDesc: "توسيع شبكة التوزيع في جميع أنحاء ليبيا",
    digitalTransformation: "التحول الرقمي",
    digitalDesc: "إطلاق نظام الطلب والتتبع الإلكتروني",
    
    // Contact
    getInTouch: "تواصل معنا",
    contactDescription: "هل لديك أسئلة حول منتجاتنا أو خدماتنا؟ نحن نحب أن نسمع منك",
    contactInfo: "معلومات الاتصال",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    address: "العنوان",
    businessHours: "ساعات العمل",
    satToThu: "السبت - الخميس",
    quickContact: "تواصل سريع عبر واتساب",
    instantResponse: "احصل على ردود فورية",
    sendMessage: "أرسل رسالة",
    fullName: "الاسم الكامل",
    phoneNumber: "رقم الهاتف",
    subject: "الموضوع",
    message: "الرسالة",
    sendViaWhatsApp: "إرسال عبر واتساب",
    findUs: "موقعنا في طرابلس",
    tripoliLibya: "طرابلس، ليبيا",
    
    // Auth
    welcomeBack: "مرحباً بعودتك",
    signInToAccount: "سجل دخولك إلى حسابك في بحر الرمال",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    signIn: "تسجيل الدخول",
    dontHaveAccount: "ليس لديك حساب؟",
    createAccount: "إنشاء حساب",
    registerWithUs: "سجل في بحر الرمال",
    confirmPassword: "تأكيد كلمة المرور",
    alreadyHaveAccount: "لديك حساب بالفعل؟",
    backToHome: "العودة للرئيسية",
    demoCredentials: "بيانات تجريبية:",
    
    // Customer Dashboard
    welcome: "مرحباً",
    manageOrders: "إدارة طلباتك وحسابك",
    totalOrders: "إجمالي الطلبات",
    pending: "قيد الانتظار",
    delivered: "تم التوصيل",
    notifications: "الإشعارات",
    recentOrders: "الطلبات الأخيرة",
    noOrdersYet: "لا توجد طلبات بعد",
    startShopping: "ابدأ التسوق",
    quickActions: "إجراءات سريعة",
    editProfile: "تعديل الملف الشخصي",
    
    // Orders
    trackOrders: "تتبع وإدارة طلباتك",
    all: "الكل",
    processing: "قيد المعالجة",
    outForDelivery: "قيد التوصيل",
    cancelled: "ملغي",
    items: "منتجات",
    total: "الإجمالي",
    driver: "السائق",
    contactDriver: "تواصل مع السائق",
    support: "الدعم",
    noOrdersFound: "لم يتم العثور على طلبات",
    
    // Favorites
    savedForLater: "المنتجات المحفوظة للشراء لاحقاً",
    noFavoritesYet: "لا توجد مفضلات بعد",
    startAddingFavorites: "ابدأ بإضافة المنتجات إلى المفضلة",
    removedFromFavorites: "تمت الإزالة من المفضلة",
    addedToFavorites: "تمت الإضافة إلى المفضلة",
    
    // Profile
    profileSettings: "إعدادات الملف الشخصي",
    manageAccount: "إدارة معلومات حسابك",
    personalInfo: "المعلومات الشخصية",
    usernameCannotChange: "لا يمكن تغيير اسم المستخدم",
    saveChanges: "حفظ التغييرات",
    accountInfo: "معلومات الحساب",
    accountType: "نوع الحساب",
    memberSince: "عضو منذ",
    accountStatus: "حالة الحساب",
    active: "نشط",
    signOut: "تسجيل الخروج",
    
    // Admin
    adminPanel: "لوحة الإدارة",
    orders: "الطلبات",
    customers: "العملاء",
    drivers: "السائقين",
    inventory: "المخزون",
    users: "المستخدمين",
    sales: "المبيعات",
    activityLogs: "سجل النشاط",
    viewSite: "عرض الموقع",
    todaysRevenue: "إيرادات اليوم",
    monthlyRevenue: "إيرادات الشهر",
    todaysOrders: "طلبات اليوم",
    pendingOrders: "طلبات معلقة",
    totalProducts: "إجمالي المنتجات",
    lowStockItems: "منتجات منخفضة المخزون",
    totalCustomers: "إجمالي العملاء",
    monthlyOrders: "طلبات الشهر",
    dailySales: "المبيعات اليومية",
    last14Days: "آخر 14 يوم",
    salesByCategory: "المبيعات حسب التصنيف",
    topSellingProducts: "المنتجات الأكثر مبيعاً",
    lowStockAlerts: "تنبيهات المخزون المنخفض",
    
    // Admin Products
    addProduct: "إضافة منتج",
    editProduct: "تعديل المنتج",
    productName: "اسم المنتج",
    arabicName: "الاسم بالعربية",
    category: "التصنيف",
    selectCategory: "اختر التصنيف",
    brand: "العلامة التجارية",
    description: "الوصف",
    costPrice: "سعر التكلفة",
    salePrice: "سعر البيع",
    discount: "الخصم",
    stockQuantity: "كمية المخزون",
    minStockAlert: "حد تنبيه المخزون",
    expiryDate: "تاريخ الانتهاء",
    batchNumber: "رقم الدفعة",
    visible: "مرئي",
    featured: "مميز",
    hidden: "مخفي",
    cancel: "إلغاء",
    create: "إنشاء",
    update: "تحديث",
    delete: "حذف",
    actions: "الإجراءات",
    price: "السعر",
    stock: "المخزون",
    status: "الحالة",
    noProducts: "لا توجد منتجات",
    
    // Admin Categories
    addCategory: "إضافة تصنيف",
    editCategory: "تعديل التصنيف",
    displayOrder: "ترتيب العرض",
    noCategories: "لا توجد تصنيفات",
    
    // Admin Orders
    order: "طلب",
    customer: "العميل",
    assign: "تعيين",
    noOrders: "لا توجد طلبات",
    
    // Admin Drivers
    addDriver: "إضافة سائق",
    vehicleType: "نوع المركبة",
    vehicleNumber: "رقم المركبة",
    deliveries: "التوصيلات",
    noDrivers: "لا يوجد سائقين",
    
    // Admin Inventory
    inventoryManagement: "إدارة المخزون",
    adjustStock: "تعديل المخزون",
    quantityChange: "التغيير في الكمية",
    reason: "السبب",
    adjust: "تعديل",
    expiringSoon: "تنتهي قريباً",
    days: "يوم",
    currentStock: "المخزون الحالي",
    minAlert: "حد التنبيه",
    noLowStock: "لا توجد منتجات منخفضة المخزون",
    noExpiring: "لا توجد منتجات تنتهي قريباً",
    noInventoryLogs: "لا توجد سجلات مخزون",
    
    // Admin Users
    userManagement: "إدارة المستخدمين",
    addUser: "إضافة مستخدم",
    editUser: "تعديل المستخدم",
    role: "الدور",
    admin: "مدير",
    staff: "موظف",
    permissions: "الصلاحيات",
    fullAccess: "صلاحيات كاملة",
    noUsers: "لا يوجد مستخدمين",
    
    // Admin Sales
    dateRange: "نطاق التاريخ",
    startDate: "تاريخ البداية",
    endDate: "تاريخ النهاية",
    applyFilter: "تطبيق الفلتر",
    totalRevenue: "إجمالي الإيرادات",
    profit: "الربح",
    avgOrderValue: "متوسط قيمة الطلب",
    last30Days: "آخر 30 يوم",
    
    // Admin Activity Logs
    allResources: "جميع الموارد",
    timestamp: "الوقت",
    user: "المستخدم",
    action: "الإجراء",
    resource: "المورد",
    details: "التفاصيل",
    noActivityLogs: "لا توجد سجلات نشاط",
    
    // Driver Dashboard
    driverPortal: "بوابة السائق",
    yourDeliveries: "توصيلاتك",
    inTransit: "قيد النقل",
    noDeliveries: "لا توجد توصيلات",
    noAssignedDeliveries: "ليس لديك توصيلات معينة حالياً",
    startDelivery: "بدء التوصيل",
    markDelivered: "تم التوصيل",
    callCustomer: "اتصل بالعميل",
    
    // Footer
    quickLinks: "روابط سريعة",
    established: "تأسست",
    allRightsReserved: "جميع الحقوق محفوظة",
    
    // Common
    lyd: "د.ل",
    loading: "جاري التحميل...",
    error: "خطأ",
    success: "نجاح",
    whatsapp: "واتساب",
    whatsappUs: "تواصل واتساب",
    product: "منتج",
    productImages: "صور المنتج",
    addImage: "إضافة صورة",
    imageAdded: "تمت إضافة الصورة",
    productUpdated: "تم تحديث المنتج",
    productCreated: "تم إنشاء المنتج",
    failedToSave: "فشل في الحفظ",
    exportPdf: "تصدير PDF",
    exportExcel: "تصدير Excel",
    reportExported: "تم تصدير التقرير",
    scan: "مسح",
    export: "تصدير",
    barcode: "الباركود",
    generateBarcode: "إنشاء باركود",
    
    // Additional translations
    productsAvailable: "منتج متاح",
    addedToCart: "تمت الإضافة للسلة",
    tryAdjustingFilters: "حاول تعديل الفلاتر أو كلمات البحث",
    category: "التصنيف",
    usa: "الولايات المتحدة",
    uk: "المملكة المتحدة",
    germany: "ألمانيا",
    france: "فرنسا",
    india: "الهند",
    
    // Invoice
    invoice: "فاتورة",
    printInvoice: "طباعة الفاتورة",
    invoiceNumber: "رقم الفاتورة",
    invoiceDate: "تاريخ الفاتورة",
    billTo: "العميل",
    itemDescription: "وصف المنتج",
    unitPrice: "سعر الوحدة",
    subtotal: "المجموع الفرعي",
    tax: "الضريبة",
    grandTotal: "الإجمالي",
    thankYou: "شكراً لتعاملكم معنا",
    
    // Import/Export
    importProducts: "استيراد المنتجات",
    exportProducts: "تصدير المنتجات",
    downloadTemplate: "تحميل القالب",
    uploadFile: "رفع ملف",
    importSuccess: "تم استيراد المنتجات بنجاح",
    importFailed: "فشل في استيراد المنتجات",
    
    // Email
    sendEmail: "إرسال بريد إلكتروني",
    emailSent: "تم إرسال البريد الإلكتروني",
    
    // Contact Page
    getInTouch: "تواصل معنا",
    contactDescription: "نحن هنا للإجابة على استفساراتك ومساعدتك في كل ما تحتاجه",
    contactInformation: "معلومات الاتصال",
    reachOutToUs: "تواصل معنا عبر أي من الوسائل التالية",
    mainOffice: "المكتب الرئيسي",
    workingHours: "ساعات العمل",
    satToThu: "السبت - الخميس",
    workTime: "9:00 صباحاً - 6:00 مساءً",
    clickToContact: "انقر للتواصل",
    chatOnWhatsApp: "تواصل عبر واتساب",
    sendMessage: "إرسال رسالة",
    yourName: "اسمك",
    enterYourName: "أدخل اسمك",
    enterYourEmail: "أدخل بريدك الإلكتروني",
    enterYourPhone: "أدخل رقم هاتفك",
    subject: "الموضوع",
    enterSubject: "أدخل الموضوع",
    message: "الرسالة",
    typeYourMessage: "اكتب رسالتك هنا...",
    sending: "جاري الإرسال...",
    messageSent: "تم إرسال رسالتك عبر واتساب",
    findUsOnMap: "موقعنا على الخريطة",
    visitOurOffice: "زيارة مكتبنا في طرابلس",
    
    // Driver Dashboard
    driverDashboard: "لوحة السائق",
    welcomeDriver: "مرحباً بك",
    pendingDeliveries: "توصيلات معلقة",
    completedToday: "مكتملة اليوم",
    inTransit: "في الطريق",
    totalDeliveries: "إجمالي التوصيلات",
    myDeliveries: "توصيلاتي",
    noDeliveriesAssigned: "لا توجد توصيلات مخصصة لك",
    checkBackLater: "تحقق لاحقاً للحصول على توصيلات جديدة",
    failedToLoadDeliveries: "فشل في تحميل التوصيلات",
    orderStatusUpdated: "تم تحديث حالة الطلب",
    failedToUpdateStatus: "فشل في تحديث الحالة",
    customer: "العميل",
    navigate: "فتح الخريطة",
    startDelivery: "بدء التوصيل",
    markDelivered: "تم التوصيل",
    shipped: "تم الشحن",
    
    // Companies Page
    ourPartners: "شركاؤنا",
    trustedPartners: "الشركات التي نتعامل معها",
    companiesDescription: "نفخر بالشراكة مع أفضل الشركات العالمية في مجال الأدوية والمكملات الغذائية",
    mainDistributor: "الموزع الرئيسي",
    authorizedDealer: "وكيل معتمد",
    internationalPartners: "شراكات دولية",
    
    // Admin labels
    admin: "المسؤول",
    
    // Additional translations for full i18n
    orderUpdated: "تم تحديث الطلب",
    failedToUpdateOrder: "فشل في تحديث الطلب",
    failedToLoadOrders: "فشل في تحميل الطلبات",
    notes: "ملاحظات",
    failedToFetchUsers: "فشل في جلب المستخدمين",
    userUpdated: "تم تحديث المستخدم",
    userCreated: "تم إنشاء المستخدم",
    userDeleted: "تم حذف المستخدم",
    failedToSaveUser: "فشل في حفظ المستخدم",
    failedToDeleteUser: "فشل في حذف المستخدم",
    cannotDeleteOwnAccount: "لا يمكنك حذف حسابك",
    deleteUserConfirm: "هل تريد حذف هذا المستخدم؟",
    contact: "الاتصال",
    categoryUpdated: "تم تحديث التصنيف",
    categoryCreated: "تم إنشاء التصنيف",
    categoryDeleted: "تم حذف التصنيف",
    failedToFetchCategories: "فشل في جلب التصنيفات",
    failedToSaveCategory: "فشل في حفظ التصنيف",
    failedToDeleteCategory: "فشل في حذف التصنيف",
    deleteCategoryConfirm: "هل تريد حذف هذا التصنيف؟",
    name: "الاسم",
    arabic: "العربية",
    stockAdjusted: "تم تعديل المخزون",
    failedToAdjustStock: "فشل في تعديل المخزون",
    failedToFetchInventory: "فشل في جلب المخزون",
    selectProduct: "اختر منتج",
    quantityChangePlaceholder: "مثال: +50 أو -10",
    reasonPlaceholder: "مثال: شحنة جديدة، بضائع تالفة...",
    date: "التاريخ",
    change: "التغيير",
    by: "بواسطة",
    expiringDays: "تنتهي قريباً (60 يوم)",
    driverAdded: "تمت إضافة السائق",
    driverRemoved: "تم حذف السائق",
    failedToFetchDrivers: "فشل في جلب السائقين",
    failedToAddDriver: "فشل في إضافة السائق",
    failedToRemoveDriver: "فشل في حذف السائق",
    removeDriverConfirm: "هل تريد حذف هذا السائق؟",
    addNewDriver: "إضافة سائق جديد",
    vehicle: "المركبة",
    noDriversYet: "لا يوجد سائقين بعد",
    failedToFetchCustomers: "فشل في جلب العملاء",
    noCustomersYet: "لا يوجد عملاء بعد",
    joined: "انضم",
    browseProductsBtn: "تصفح المنتجات",
    myFavoritesBtn: "مفضلاتي",
    editProfileBtn: "تعديل الملف",
    noNotifications: "لا توجد إشعارات",
    viewAllOrders: "عرض جميع الطلبات",
    youHaventPlacedOrders: "لم تقم بأي طلبات بعد",
    noFilteredOrders: "لا توجد طلبات",
    qty: "الكمية",
    driverInfo: "معلومات السائق",
    profileUpdated: "تم تحديث الملف الشخصي",
    failedToUpdateProfile: "فشل في تحديث الملف الشخصي",
    yourFullName: "اسمك الكامل",
    yourEmail: "بريدك الإلكتروني",
    saving: "جاري الحفظ...",
    noFavoritesDesc: "ابدأ بإضافة المنتجات للمفضلة لرؤيتها هنا",
    removedFromFavorites: "تمت الإزالة من المفضلة",
    failedToRemoveFavorite: "فشل في الإزالة من المفضلة",
    addedToCartMsg: "تمت الإضافة للسلة",
    passwordsNoMatch: "كلمات المرور غير متطابقة",
    passwordTooShort: "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
    accountCreated: "تم إنشاء الحساب بنجاح",
    registrationFailed: "فشل في التسجيل",
    creatingAccount: "جاري إنشاء الحساب...",
    chooseUsername: "اختر اسم مستخدم",
    createPassword: "أنشئ كلمة مرور",
    confirmYourPassword: "أكد كلمة المرور",
    loginFailed: "فشل تسجيل الدخول",
    left: "متبقي",
    inactive: "غير نشط",
    
    // Cart translations
    shoppingCart: "سلة التسوق",
    itemsInCart: "منتجات في السلة",
    cartIsEmpty: "السلة فارغة",
    startAddingProducts: "ابدأ بإضافة المنتجات للسلة",
    remove: "إزالة",
    subtotal: "المجموع الفرعي",
    orderSummary: "ملخص الطلب",
    shipping: "الشحن",
    free: "مجاني",
    deliveryFee: "رسوم التوصيل",
    shippingAddress: "عنوان الشحن",
    enterShippingAddress: "أدخل عنوان الشحن",
    orderNotes: "ملاحظات إضافية (اختياري)",
    checkout: "إتمام الطلب",
    processing: "جاري المعالجة...",
    pleaseLoginToCheckout: "الرجاء تسجيل الدخول لإتمام الطلب",
    pleaseEnterAddress: "الرجاء إدخال عنوان الشحن",
    orderPlacedSuccessfully: "تم تقديم الطلب بنجاح",
    failedToPlaceOrder: "فشل في تقديم الطلب",
    
    // Driver translations
    orderDelivered: "تم تسليم الطلب بنجاح",
    orderCancelled: "تم إلغاء الطلب",
    orderReturned: "تم إرجاع الطلب",
    customerNotFound: "تم تسجيل عدم العثور على العميل",
    failedToLoadDeliveries: "فشل في تحميل التوصيلات",
    confirm: "تأكيد",
    
    // Guest checkout translations
    customerInfo: "معلومات العميل",
    companyName: "اسم الشركة / الصيدلية",
    enterFullName: "أدخل اسمك الكامل",
    enterCompanyName: "أدخل اسم الشركة أو الصيدلية",
    pleaseEnterName: "الرجاء إدخال الاسم",
    pleaseEnterCompany: "الرجاء إدخال اسم الشركة",
    pleaseEnterPhone: "الرجاء إدخال رقم الهاتف",
    haveAccount: "لديك حساب؟",
    orderConfirmationSent: "تم إرسال تأكيد الطلب",
    
    // Delivery & Payment translations
    deliveryOptions: "خيارات التوصيل",
    customDeliveryPrice: "سعر التوصيل المخصص (د.ل)",
    paymentMethod: "طريقة الدفع",
    deliveryType: "نوع التسليم",
    productTotal: "إجمالي المنتجات",
    placeOrder: "تأكيد الطلب",
  },
  en: {
    // Navigation
    home: "Home",
    about: "About",
    products: "Products",
    contact: "Contact",
    login: "Login",
    register: "Register",
    logout: "Logout",
    dashboard: "Dashboard",
    myOrders: "My Orders",
    favorites: "Favorites",
    profile: "Profile",
    
    // Hero
    heroTitle: "Bahr Al Rimal",
    heroSubtitle: "Medical Supplies Import & Distribution",
    heroDescription: "Main distributor for Lynae Vitamins and Supplements in Libya",
    browseProducts: "Browse Products",
    contactUs: "Contact Us",
    
    // Features
    qualityProducts: "Quality Products",
    qualityDesc: "Premium pharmaceuticals and medical supplies from trusted global manufacturers",
    fastDelivery: "Fast Delivery",
    fastDeliveryDesc: "Reliable distribution network across Libya with same-day delivery options",
    certifiedSafe: "Certified & Safe",
    certifiedSafeDesc: "All products meet international quality and safety standards",
    since2012: "Since 2012",
    since2012Desc: "Over a decade of trusted medical distribution excellence",
    
    // Products
    featuredProducts: "Featured Products",
    popularProducts: "Popular Products",
    viewAll: "View All",
    addToCart: "Add to Cart",
    add: "Add",
    categories: "Categories",
    browseByCategory: "Browse by Category",
    allCategories: "All Categories",
    search: "Search",
    searchProducts: "Search products...",
    filters: "Filters",
    clearAll: "Clear All",
    priceRange: "Price Range",
    countryOfOrigin: "Country of Origin",
    allCountries: "All Countries",
    noProductsFound: "No Products Found",
    tryAdjusting: "Try adjusting your filters or search terms",
    clearFilters: "Clear Filters",
    lowStock: "Low Stock",
    inStock: "In Stock",
    outOfStock: "Out of Stock",
    
    // Product Detail
    orderViaWhatsApp: "Order via WhatsApp",
    quantity: "Quantity",
    sku: "SKU",
    origin: "Origin",
    expiry: "Expiry",
    batch: "Batch",
    relatedProducts: "Related Products",
    
    // About
    aboutUs: "About Us",
    ourStory: "Our Story",
    storyText1: "Founded in 2012 in Tripoli, Bahr Al Rimal has grown to become one of Libya's leading medical distribution companies.",
    storyText2: "We specialize in importing and distributing pharmaceuticals, vitamins, supplements, and medical equipment. As the exclusive main distributor for Lynae in Libya, we bring world-class healthcare products.",
    keyMilestones: "Key Milestones",
    ourValues: "Our Values",
    qualityFirst: "Quality First",
    customerFocus: "Customer Focus",
    reliableDelivery: "Reliable Delivery",
    globalSourcing: "Global Sourcing",
    lynaePartnership: "Lynae Partnership",
    lynaeDesc: "As the exclusive main distributor for Lynae in Libya, we bring premium American vitamins and supplements",
    viewLynaeProducts: "View Lynae Products",
    partnerWithUs: "Partner With Us",
    
    // Contact
    getInTouch: "Get in Touch",
    contactDescription: "Have questions about our products or services? We'd love to hear from you",
    contactInfo: "Contact Information",
    phone: "Phone",
    email: "Email",
    address: "Address",
    businessHours: "Business Hours",
    satToThu: "Saturday - Thursday",
    quickContact: "Quick Contact via WhatsApp",
    instantResponse: "Get instant responses",
    sendMessage: "Send us a Message",
    fullName: "Full Name",
    phoneNumber: "Phone Number",
    subject: "Subject",
    message: "Message",
    sendViaWhatsApp: "Send via WhatsApp",
    findUs: "Find Us in Tripoli",
    tripoliLibya: "Tripoli, Libya",
    
    // Auth
    welcomeBack: "Welcome Back",
    signInToAccount: "Sign in to your Bahr Al Rimal account",
    username: "Username",
    password: "Password",
    signIn: "Sign In",
    dontHaveAccount: "Don't have an account?",
    createAccount: "Create Account",
    registerWithUs: "Register with Bahr Al Rimal",
    confirmPassword: "Confirm Password",
    alreadyHaveAccount: "Already have an account?",
    backToHome: "Back to Home",
    demoCredentials: "Demo Credentials:",
    
    // Customer Dashboard
    welcome: "Welcome",
    manageOrders: "Manage your orders and account",
    totalOrders: "Total Orders",
    pending: "Pending",
    delivered: "Delivered",
    notifications: "Notifications",
    recentOrders: "Recent Orders",
    noOrdersYet: "No orders yet",
    startShopping: "Start Shopping",
    quickActions: "Quick Actions",
    editProfile: "Edit Profile",
    
    // Orders
    trackOrders: "Track and manage your orders",
    all: "All",
    processing: "Processing",
    outForDelivery: "Out for Delivery",
    cancelled: "Cancelled",
    items: "items",
    total: "Total",
    driver: "Driver",
    contactDriver: "Contact Driver",
    support: "Support",
    noOrdersFound: "No orders found",
    
    // Favorites
    savedForLater: "Products you've saved for later",
    noFavoritesYet: "No favorites yet",
    startAddingFavorites: "Start adding products to your favorites",
    removedFromFavorites: "Removed from favorites",
    addedToFavorites: "Added to favorites",
    
    // Profile
    profileSettings: "Profile Settings",
    manageAccount: "Manage your account information",
    personalInfo: "Personal Information",
    usernameCannotChange: "Username cannot be changed",
    saveChanges: "Save Changes",
    accountInfo: "Account Information",
    accountType: "Account Type",
    memberSince: "Member Since",
    accountStatus: "Account Status",
    active: "Active",
    signOut: "Sign Out",
    
    // Admin
    adminPanel: "Admin Panel",
    orders: "Orders",
    customers: "Customers",
    drivers: "Drivers",
    inventory: "Inventory",
    users: "Users",
    sales: "Sales",
    activityLogs: "Activity Logs",
    viewSite: "View Site",
    todaysRevenue: "Today's Revenue",
    monthlyRevenue: "Monthly Revenue",
    todaysOrders: "Today's Orders",
    pendingOrders: "Pending Orders",
    totalProducts: "Total Products",
    lowStockItems: "Low Stock Items",
    totalCustomers: "Total Customers",
    monthlyOrders: "Monthly Orders",
    dailySales: "Daily Sales",
    last14Days: "Last 14 Days",
    salesByCategory: "Sales by Category",
    topSellingProducts: "Top Selling Products",
    lowStockAlerts: "Low Stock Alerts",
    
    // Admin Products
    addProduct: "Add Product",
    editProduct: "Edit Product",
    productName: "Product Name",
    arabicName: "Arabic Name",
    category: "Category",
    selectCategory: "Select category",
    brand: "Brand",
    description: "Description",
    costPrice: "Cost Price",
    salePrice: "Sale Price",
    discount: "Discount",
    stockQuantity: "Stock Quantity",
    minStockAlert: "Min Stock Alert",
    expiryDate: "Expiry Date",
    batchNumber: "Batch Number",
    visible: "Visible",
    featured: "Featured",
    hidden: "Hidden",
    cancel: "Cancel",
    create: "Create",
    update: "Update",
    delete: "Delete",
    actions: "Actions",
    price: "Price",
    stock: "Stock",
    status: "Status",
    noProducts: "No products",
    
    // Admin Categories
    addCategory: "Add Category",
    editCategory: "Edit Category",
    displayOrder: "Display Order",
    noCategories: "No categories",
    
    // Admin Orders
    order: "Order",
    customer: "Customer",
    assign: "Assign",
    noOrders: "No orders",
    
    // Admin Drivers
    addDriver: "Add Driver",
    vehicleType: "Vehicle Type",
    vehicleNumber: "Vehicle Number",
    deliveries: "Deliveries",
    noDrivers: "No drivers",
    
    // Admin Inventory
    inventoryManagement: "Inventory Management",
    adjustStock: "Adjust Stock",
    quantityChange: "Quantity Change",
    reason: "Reason",
    adjust: "Adjust",
    expiringSoon: "Expiring Soon",
    days: "days",
    currentStock: "Current Stock",
    minAlert: "Min Alert",
    noLowStock: "No low stock items",
    noExpiring: "No products expiring soon",
    noInventoryLogs: "No inventory logs",
    
    // Admin Users
    userManagement: "User Management",
    addUser: "Add User",
    editUser: "Edit User",
    role: "Role",
    admin: "Admin",
    staff: "Staff",
    permissions: "Permissions",
    fullAccess: "Full Access",
    noUsers: "No users",
    
    // Admin Sales
    dateRange: "Date Range",
    startDate: "Start Date",
    endDate: "End Date",
    applyFilter: "Apply Filter",
    totalRevenue: "Total Revenue",
    profit: "Profit",
    avgOrderValue: "Avg Order Value",
    last30Days: "Last 30 Days",
    
    // Admin Activity Logs
    allResources: "All Resources",
    timestamp: "Timestamp",
    user: "User",
    action: "Action",
    resource: "Resource",
    details: "Details",
    noActivityLogs: "No activity logs",
    
    // Driver Dashboard
    driverPortal: "Driver Portal",
    yourDeliveries: "Your Deliveries",
    inTransit: "In Transit",
    noDeliveries: "No Deliveries",
    noAssignedDeliveries: "You don't have any assigned deliveries right now",
    startDelivery: "Start Delivery",
    markDelivered: "Mark Delivered",
    callCustomer: "Call Customer",
    
    // Footer
    quickLinks: "Quick Links",
    established: "Established",
    allRightsReserved: "All rights reserved",
    
    // Common
    lyd: "LYD",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    whatsapp: "WhatsApp",
    whatsappUs: "WhatsApp Us",
    product: "Product",
    productImages: "Product Images",
    addImage: "Add Image",
    imageAdded: "Image added",
    productUpdated: "Product updated",
    productCreated: "Product created",
    failedToSave: "Failed to save",
    exportPdf: "Export PDF",
    exportExcel: "Export Excel",
    reportExported: "Report exported",
    scan: "Scan",
    export: "Export",
    barcode: "Barcode",
    generateBarcode: "Generate Barcode",
    
    // Additional translations
    productsAvailable: "products available",
    addedToCart: "Added to cart",
    tryAdjustingFilters: "Try adjusting your filters or search terms",
    category: "Category",
    usa: "USA",
    uk: "UK",
    germany: "Germany",
    france: "France",
    india: "India",
    
    // Invoice
    invoice: "Invoice",
    printInvoice: "Print Invoice",
    invoiceNumber: "Invoice Number",
    invoiceDate: "Invoice Date",
    billTo: "Bill To",
    itemDescription: "Item Description",
    unitPrice: "Unit Price",
    subtotal: "Subtotal",
    tax: "Tax",
    grandTotal: "Grand Total",
    thankYou: "Thank you for your business",
    
    // Import/Export
    importProducts: "Import Products",
    exportProducts: "Export Products",
    downloadTemplate: "Download Template",
    uploadFile: "Upload File",
    importSuccess: "Products imported successfully",
    importFailed: "Failed to import products",
    
    // Email
    sendEmail: "Send Email",
    emailSent: "Email sent successfully",
    
    // Contact Page
    getInTouch: "Get In Touch",
    contactDescription: "We're here to answer your questions and help with your needs",
    contactInformation: "Contact Information",
    reachOutToUs: "Reach out to us through any of the following",
    mainOffice: "Main Office",
    workingHours: "Working Hours",
    satToThu: "Saturday - Thursday",
    workTime: "9:00 AM - 6:00 PM",
    clickToContact: "Click to contact",
    chatOnWhatsApp: "Chat on WhatsApp",
    sendMessage: "Send Message",
    yourName: "Your Name",
    enterYourName: "Enter your name",
    enterYourEmail: "Enter your email",
    enterYourPhone: "Enter your phone",
    subject: "Subject",
    enterSubject: "Enter subject",
    message: "Message",
    typeYourMessage: "Type your message here...",
    sending: "Sending...",
    messageSent: "Message sent via WhatsApp",
    findUsOnMap: "Find Us on Map",
    visitOurOffice: "Visit our office in Tripoli",
    
    // Driver Dashboard
    driverDashboard: "Driver Dashboard",
    welcomeDriver: "Welcome",
    pendingDeliveries: "Pending Deliveries",
    completedToday: "Completed Today",
    inTransit: "In Transit",
    totalDeliveries: "Total Deliveries",
    myDeliveries: "My Deliveries",
    noDeliveriesAssigned: "No deliveries assigned",
    checkBackLater: "Check back later for new deliveries",
    failedToLoadDeliveries: "Failed to load deliveries",
    orderStatusUpdated: "Order status updated",
    failedToUpdateStatus: "Failed to update status",
    customer: "Customer",
    navigate: "Navigate",
    startDelivery: "Start Delivery",
    markDelivered: "Mark Delivered",
    shipped: "Shipped",
    
    // Companies Page
    ourPartners: "Our Partners",
    trustedPartners: "Companies We Work With",
    companiesDescription: "We are proud to partner with leading global pharmaceutical and supplement companies",
    mainDistributor: "Main Distributor",
    authorizedDealer: "Authorized Dealer",
    internationalPartners: "International Partners",
    
    // Admin labels
    admin: "Admin",
    
    // Additional translations for full i18n
    orderUpdated: "Order updated",
    failedToUpdateOrder: "Failed to update order",
    failedToLoadOrders: "Failed to load orders",
    notes: "Notes",
    failedToFetchUsers: "Failed to fetch users",
    userUpdated: "User updated",
    userCreated: "User created",
    userDeleted: "User deleted",
    failedToSaveUser: "Failed to save user",
    failedToDeleteUser: "Failed to delete user",
    cannotDeleteOwnAccount: "Cannot delete your own account",
    deleteUserConfirm: "Delete this user?",
    contact: "Contact",
    categoryUpdated: "Category updated",
    categoryCreated: "Category created",
    categoryDeleted: "Category deleted",
    failedToFetchCategories: "Failed to fetch categories",
    failedToSaveCategory: "Failed to save category",
    failedToDeleteCategory: "Failed to delete category",
    deleteCategoryConfirm: "Delete this category?",
    name: "Name",
    arabic: "Arabic",
    stockAdjusted: "Stock adjusted",
    failedToAdjustStock: "Failed to adjust stock",
    failedToFetchInventory: "Failed to fetch inventory",
    selectProduct: "Select product",
    quantityChangePlaceholder: "e.g., +50 or -10",
    reasonPlaceholder: "e.g., New shipment, Damaged goods...",
    date: "Date",
    change: "Change",
    by: "By",
    expiringDays: "Expiring Soon (60 days)",
    driverAdded: "Driver added",
    driverRemoved: "Driver removed",
    failedToFetchDrivers: "Failed to fetch drivers",
    failedToAddDriver: "Failed to add driver",
    failedToRemoveDriver: "Failed to remove driver",
    removeDriverConfirm: "Remove this driver?",
    addNewDriver: "Add New Driver",
    vehicle: "Vehicle",
    noDriversYet: "No drivers yet",
    failedToFetchCustomers: "Failed to fetch customers",
    noCustomersYet: "No customers yet",
    joined: "Joined",
    browseProductsBtn: "Browse Products",
    myFavoritesBtn: "My Favorites",
    editProfileBtn: "Edit Profile",
    noNotifications: "No notifications",
    viewAllOrders: "View All Orders",
    youHaventPlacedOrders: "You haven't placed any orders yet.",
    noFilteredOrders: "No orders found",
    qty: "Qty",
    driverInfo: "Driver Info",
    profileUpdated: "Profile updated successfully",
    failedToUpdateProfile: "Failed to update profile",
    yourFullName: "Your full name",
    yourEmail: "Your email",
    saving: "Saving...",
    noFavoritesDesc: "Start adding products to your favorites to see them here.",
    removedFromFavorites: "Removed from favorites",
    failedToRemoveFavorite: "Failed to remove from favorites",
    addedToCartMsg: "added to cart",
    passwordsNoMatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters",
    accountCreated: "Account created successfully!",
    registrationFailed: "Registration failed",
    creatingAccount: "Creating Account...",
    chooseUsername: "Choose a username",
    createPassword: "Create a password",
    confirmYourPassword: "Confirm your password",
    loginFailed: "Login failed",
    left: "left",
    inactive: "Inactive",
    
    // Cart translations
    shoppingCart: "Shopping Cart",
    itemsInCart: "items in cart",
    cartIsEmpty: "Your cart is empty",
    startAddingProducts: "Start adding products to your cart",
    remove: "Remove",
    subtotal: "Subtotal",
    orderSummary: "Order Summary",
    shipping: "Shipping",
    free: "Free",
    deliveryFee: "Delivery Fee",
    shippingAddress: "Shipping Address",
    enterShippingAddress: "Enter your shipping address",
    orderNotes: "Additional notes (optional)",
    checkout: "Checkout",
    processing: "Processing...",
    pleaseLoginToCheckout: "Please login to checkout",
    pleaseEnterAddress: "Please enter shipping address",
    orderPlacedSuccessfully: "Order placed successfully!",
    failedToPlaceOrder: "Failed to place order",
    
    // Driver translations
    orderDelivered: "Order delivered successfully",
    orderCancelled: "Order cancelled",
    orderReturned: "Order returned",
    customerNotFound: "Customer not found recorded",
    failedToLoadDeliveries: "Failed to load deliveries",
    confirm: "Confirm",
    
    // Guest checkout translations
    customerInfo: "Customer Information",
    companyName: "Company / Pharmacy Name",
    enterFullName: "Enter your full name",
    enterCompanyName: "Enter company or pharmacy name",
    pleaseEnterName: "Please enter your name",
    pleaseEnterCompany: "Please enter company name",
    pleaseEnterPhone: "Please enter phone number",
    haveAccount: "Have an account?",
    orderConfirmationSent: "Order confirmation sent",
    
    // Delivery & Payment translations
    deliveryOptions: "Delivery Options",
    customDeliveryPrice: "Custom Delivery Price (LYD)",
    paymentMethod: "Payment Method",
    deliveryType: "Delivery Type",
    productTotal: "Product Total",
    placeOrder: "Place Order",
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved || "ar"; // Default to Arabic
  });

  useEffect(() => {
    localStorage.setItem("language", language);
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "ar" ? "en" : "ar"));
  };

  const isRTL = language === "ar";

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};
